import {createSelector} from 'reselect';

const sidebarSegmentOrder = ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'FIFTH', 'SIXTH'];

const getShoppingCart = (store) => store.shoppingCart.shoppingCart || {};
const getAncillariesInformation = (store) => store.ancillariesInformation || {};
const getSegments = (store) => getShoppingCart(store).segments || [];
const getProducts = (store) => store.productSelection.products || [];
const getRequiredTravellerInformation = (shoppingCart) => shoppingCart.requiredTravellerInformation || [];
const hasPieces = (bagConditions) => bagConditions.baggageDescriptorIncludedInPrice.pieces > 0;
const getNumTravellerWithBags = (requiredTravellerInfo) => requiredTravellerInfo.filter((info) => info.baggageConditions.some(hasPieces)).length;
const noInfantTraveller = (bagConditions) => bagConditions.travellerType !== 'INFANT';
const filterTraveller = (requiredTravellerInfo) => requiredTravellerInfo.baggageConditions.filter(noInfantTraveller)[0] || {};
const getTravellerBags = (requiredTravellerInfo) => requiredTravellerInfo.filter(filterTraveller)[0] || {};
const getBagsConditions = (requiredTravellerInfo) => getTravellerBags(requiredTravellerInfo).baggageConditions || [];
const getTripType = (segments) => {
    const sameOriginAndDestination = segments.length === 2 && segments[0].departureLocation.geoNodeId === segments[1].arrivalLocation.geoNodeId;
    let tripType;
    if (segments.length === 1) {
        tripType = 'ONE_WAY';
    } else if (sameOriginAndDestination) {
        tripType = 'ROUND_TRIP';
    } else {
        tripType = 'MULTIPLE_DESTINATIONS';
    }
    return tripType;
};
const getBagsWithPieces = (bag) => bag.selection.pieces > 0;
const compareBags = (oneBag, otherBag) =>
    oneBag.kilosPerPiece === otherBag.kilosPerPiece && oneBag.price === otherBag.price && oneBag.idRow === otherBag.idRow || false;
const groupSegmentBags = (origBag, bag, addWeight) => {
    origBag.selection.pieces += bag.selection.pieces;
    origBag.selection.piecesToShow += bag.selection.piecesToShow;
    origBag.price.total += bag.price.total;
    origBag.perks.fromClient += (bag.perks.fromClient || 0);
    origBag.perks.fromDapi += (bag.perks.fromDapi || 0);
    origBag.quantity += bag.quantity;
    if (addWeight) {
        origBag.selection.kilos += bag.selection.kilos;
    }
    return origBag;
};
const getBagsFromTravellerInfo = (requiredTravellerInfo) => {
    const numBags = getNumTravellerWithBags(requiredTravellerInfo);
    return getBagsConditions(requiredTravellerInfo).map((bag) => {
        return Object.assign({}, bag, {
            baggageDescriptorIncludedInPrice: {
                ...bag.baggageDescriptorIncludedInPrice,
                pieces: bag.baggageDescriptorIncludedInPrice.pieces * numBags,
            },
        });
    });
};
const addTrainBags = (bagsConditions, segmentList, numTravellers) => {
    segmentList.forEach((segment) => {
        if (segment.transportType === 'TRAIN') {
            let trainBag = bagsConditions.filter((bag) => bag.segmentTypeIndex === sidebarSegmentOrder[segment.id])[0] || {};
            if (Object.keys(trainBag).length) {
                trainBag.baggageDescriptorIncludedInPrice.pieces = ['2A', 'R1'].includes(segment.carrier.code) ? 1 : 2;
                trainBag.baggageDescriptorIncludedInPrice.pieces *= numTravellers;
            }
        }
    });

    return bagsConditions;
};
const updateBaggage = (bagsConditions, segmentList, numTravellers) => {
    if (bagsConditions[0] && !bagsConditions[0].segmentTypeIndex) {
        bagsConditions[0].segmentTypeIndex = 'FIRST';
    }

    if (bagsConditions && bagsConditions.length === 1 && bagsConditions[0].baggageApplicationLevel === 'ITINERARY'
            && segmentList.length > bagsConditions.length) {
        bagsConditions = bagsConditions.concat(Object.assign({}, bagsConditions[0], {segmentTypeIndex: 'SECOND'}));
    }

    return addTrainBags(bagsConditions, segmentList, numTravellers);
};
const sortBags = (bagsConditions) => {
    if (bagsConditions.length > 1) {
        bagsConditions.sort(function(a, b) {
            return sidebarSegmentOrder.indexOf(a.segmentTypeIndex) - sidebarSegmentOrder.indexOf(b.segmentTypeIndex);
        });
    }
    return bagsConditions;
};
const updateSegmentBag = (checkInBags) => {
    return checkInBags.map((bag) => Object.assign({}, bag,
        {
            idRow: 'SECOND',
            name: bag.name.replace('FIRST', 'SECOND'),
            id: bag.id.replace('FIRST', 'SECOND'),
            selection: {
                ...bag.selection,
                idRow: 'SECOND',
            },
        }
    ));
};
const getCheckInBagsData = (products, segments, shoppingCart, price) => {
    const checkInBags = JSON.parse(JSON.stringify(getProductByCategory(products, 'BAGGAGE')));
    const requiredTravellerInfo = getRequiredTravellerInformation(shoppingCart);
    const allItineraryBags = getBagsConditions(requiredTravellerInfo).every((bag) => bag.baggageApplicationLevel === 'ITINERARY');
    if (!price && getTripType(segments) === 'ROUND_TRIP' && checkInBags.length > 0
        && allItineraryBags && checkInBags.every((bag) => bag.idRow === 'FIRST')) {
        return checkInBags.concat(updateSegmentBag(checkInBags));
    }
    return checkInBags;
};
const groupCheckInBags = (checkInBags, addWeight) => {
    return checkInBags.filter(getBagsWithPieces).reduce((accBags, currentBag) => {
        const existingBag = accBags.filter((bag) => compareBags(bag.selection, currentBag.selection)) || [];
        if (existingBag.length === 0) {
            accBags.push(currentBag);
        } else {
            groupSegmentBags(existingBag[0], currentBag, addWeight);
        }
        return accBags;
    }, []);
};
const getProductByCategory = (products, productCategory) => {
    return products.filter((product) => productCategory === product['category']);
};

const getIncludedBags = (shoppingCart, segments) => {
    const requiredTravellerInfo = getRequiredTravellerInformation(shoppingCart);
    const bagsConditions = getBagsFromTravellerInfo(requiredTravellerInfo);
    const numTravellers = requiredTravellerInfo.filter(noInfantTraveller).length;
    return sortBags(updateBaggage(bagsConditions, segments, numTravellers));
};
const getCheckInBags = (products, segments, shoppingCart, price, addWeight) => {
    const checkInBags = getCheckInBagsData(products, segments, shoppingCart, price);
    return groupCheckInBags(checkInBags, addWeight);
};

const includedBagsSelector = createSelector(getShoppingCart, getSegments, getIncludedBags);
const checkInBagsViewSelector = createSelector(getProducts, getSegments, getShoppingCart, () => false, () => false, getCheckInBags);
const checkInBagsAddWeightViewSelector = createSelector(getProducts, getSegments, getShoppingCart, () => false, () => true, getCheckInBags);
const checkInBagsPriceSelector = createSelector(getProducts, getSegments, getShoppingCart, () => true, () => false, getCheckInBags);
const checkInBagsMerchandisingInformationSelector = createSelector(getAncillariesInformation);

export default {
    includedBags: (store) => includedBagsSelector(store),
    checkInBagsView: (store) => checkInBagsViewSelector(store),
    checkInBagsAddWeightView: (store) => checkInBagsAddWeightViewSelector(store),
    checkInBagsPrice: (store) => checkInBagsPriceSelector(store),
    checkInBagsMerchandisingInformation: (store) => checkInBagsMerchandisingInformationSelector(store),
};
